"use client";

import { Mail, Phone, MapPin, Instagram, Twitter, Facebook, Youtube, Linkedin, Loader, ArrowRight, CircleCheck as CheckCircle2 } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Link } from "@/components/common/Link";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const socialLinks = [{
  label: "YOUTUBE",
  icon: Youtube,
  href: "https://www.youtube.com/channel/UCKZXfMhbDxGBul5C6W-qWqg"
}, {
  label: "LINKEDIN",
  icon: Linkedin,
  href: "https://www.linkedin.com/company/imcncdesigns"
}, {
  label: "INSTAGRAM",
  icon: Instagram,
  href: "https://www.instagram.com/imcncdesigns/"
}, {
  label: "FACEBOOK",
  icon: Facebook,
  href: "https://www.facebook.com/imcnc.designs/"
}];
function HexIcon({
  children
}: {
  children: React.ReactNode;
}) {
  return <div className="flex items-center justify-center w-11 h-11 shrink-0 bg-primary/10 text-primary" style={{
    clipPath: "polygon(0% 0%, 100% 0%, 100% 100%, 0% 100%)"
  }}>
      {children}
    </div>;
}
function ContactForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="p-8 bg-primary/5 border border-primary/20 rounded-md">
        <h3 className="font-default text-2xl text-foreground mb-3">Request Logged</h3>
        <p className="text-foreground/80 mb-6">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="rounded-md border-primary text-primary hover:bg-primary hover:text-white">
          Submit New Inquiry
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-8">
      <FormField control={control} name="name" rules={{
      required: "Please enter your name",
      minLength: {
        value: 2,
        message: "At least 2 characters"
      }
    }} render={({
      field
    }) => <FormItem className="gap-2">
            <FormLabel className="font-mono text-[12px] uppercase tracking-widest text-muted-foreground">
              [FULL_NAME]
            </FormLabel>
            <FormControl>
              <Input placeholder="Enter your name" {...field} className="h-11 px-0 bg-transparent border-0 border-b border-border focus-visible:ring-0 focus-visible:border-primary focus-visible:border-b-2 text-base text-foreground placeholder:text-muted-foreground/40 transition-all rounded-md" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="email" rules={{
      required: "Email is required",
      pattern: {
        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
        message: "Please enter a valid email"
      }
    }} render={({
      field
    }) => <FormItem className="gap-2">
            <FormLabel className="font-mono text-[12px] uppercase tracking-widest text-muted-foreground">
              [EMAIL_ADDRESS]
            </FormLabel>
            <FormControl>
              <Input type="email" placeholder="charles@example.co.za" {...field} className="h-11 px-0 bg-transparent border-0 border-b border-border focus-visible:ring-0 focus-visible:border-primary focus-visible:border-b-2 text-base text-foreground placeholder:text-muted-foreground/40 transition-all rounded-md" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="phone" rules={{
      pattern: {
        value: /^[+]?[\d\s()-]{7,}$/,
        message: "Please enter a valid phone number"
      }
    }} render={({
      field
    }) => <FormItem className="gap-2">
            <FormLabel className="font-mono text-[12px] uppercase tracking-widest text-muted-foreground">
              [CONTACT_NUMBER]
            </FormLabel>
            <FormControl>
              <Input type="tel" placeholder="082 553 8657" {...field} className="h-11 px-0 bg-transparent border-0 border-b border-border focus-visible:ring-0 focus-visible:border-primary focus-visible:border-b-2 text-base text-foreground placeholder:text-muted-foreground/40 transition-all rounded-md" />
            </FormControl>
            <p className="font-mono text-[11px] text-muted-foreground/70">
              Required for technical coordination
            </p>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="inquiry_type" render={({
      field
    }) => <FormItem className="gap-2">
            <FormLabel className="font-mono text-[12px] uppercase tracking-widest text-muted-foreground">
              [SERVICE_CATEGORY]
            </FormLabel>
            <Select onValueChange={field.onChange} value={field.value}>
              <FormControl>
                <SelectTrigger className="h-11 w-full px-0 bg-transparent border-0 border-b border-border focus-visible:ring-0 focus-visible:border-primary text-base text-foreground data-[placeholder]:text-muted-foreground/40 rounded-md">
                  <SelectValue placeholder="Select technical service" />
                </SelectTrigger>
              </FormControl>
              <SelectContent className="rounded-md border-border">
                <SelectItem value="siemens-parts">Siemens Spare Parts</SelectItem>
                <SelectItem value="cnc-retrofitting">CNC Retrofitting</SelectItem>
                <SelectItem value="field-services">Field Services</SelectItem>
                <SelectItem value="technical-support">Technical Support</SelectItem>
                <SelectItem value="general">General Inquiry</SelectItem>
              </SelectContent>
            </Select>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="message" rules={{
      required: "Please share a message",
      minLength: {
        value: 10,
        message: "At least 10 characters"
      }
    }} render={({
      field
    }) => <FormItem className="gap-2">
            <FormLabel className="font-mono text-[12px] uppercase tracking-widest text-muted-foreground">
              [TECHNICAL_SPECIFICATIONS]
            </FormLabel>
            <FormControl>
              <Textarea placeholder="Describe your CNC requirements or part numbers..." rows={4} {...field} className="px-0 bg-transparent border-0 border-b border-border focus-visible:ring-0 focus-visible:border-primary focus-visible:border-b-2 text-base text-foreground placeholder:text-muted-foreground/40 transition-all resize-none rounded-md" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-4 bg-destructive/10 border border-destructive/30 rounded-md">
          <p className="text-sm text-destructive">{submitError}</p>
        </div>}

      <Button type="submit" disabled={isSubmitting} size="lg" className="w-full h-14 rounded-md bg-accent text-white hover:brightness-110 font-bold text-base shadow-md transition-all">
        {isSubmitting ? <>
            <Loader className="animate-spin mr-2 size-4" />
            TRANSMITTING...
          </> : <>
            CONTACT AN EXPERT
            <ArrowRight className="size-4 ml-2" />
          </>}
      </Button>
    </Form>;
}
export default function ServiceInquiry() {
  const {
    ref: leftRef,
    isVisible: leftVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const {
    ref: rightRef,
    isVisible: rightVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const email = "charles@imcncdesigns.co.za";
  const phone = "082 553 8657";
  const address = "Wilge rd., Golfpark, Gauteng, South Africa, 1961";
  const serviceTypes = ["CNC Machine Diagnostics & Repair", "Siemens Sinumerik System Integration", "Preventative Maintenance Programs", "On-site Technical Training", "Emergency Breakdown Support"];
  const benefits = ["Reduced machine downtime through expert diagnostics", "Direct access to Siemens certified spare parts", "Optimized machine performance and accuracy", "Extended equipment lifespan with professional care"];
  return <section data-section-id="960" id="service-inquiry" className="relative bg-background text-foreground py-24 md:py-[120px] overflow-hidden">
      <div className="container mx-auto px-6 lg:px-10 max-w-7xl">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          <div ref={leftRef} className={`lg:col-span-5 flex flex-col transition-all duration-700 ease-out ${leftVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
            <span className="font-mono text-xs uppercase tracking-widest text-primary mb-6">SERVICE INQUIRY</span>

            <h2 className="font-default font-bold text-foreground leading-tight text-4xl sm:text-5xl lg:text-[56px] mb-6">
              Precision{" "}
              <span className="text-primary font-bold">Support</span>{" "}
              for Your Operations
            </h2>

            <p className="font-default text-[19px] leading-relaxed text-foreground/80 max-w-md mb-12">
              Schedule an onsite appointment and let us assist you with all your CNC retrofitting, services and Siemens spare parts required. Our engineers are ready to optimize your industrial workflow.
            </p>

            <div className="flex flex-col gap-6 mb-12">
              <div className="flex items-center gap-4">
                <HexIcon>
                  <Mail className="size-4" />
                </HexIcon>
                <div className="flex flex-col">
                  <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mb-1">
                    Technical Email
                  </span>
                  <Link to={`mailto:${email}`} className="text-foreground hover:text-primary transition-colors text-base font-medium font-default">
                    {email}
                  </Link>
                </div>
              </div>

              <div className="flex items-center gap-4">
                <HexIcon>
                  <Phone className="size-4" />
                </HexIcon>
                <div className="flex flex-col">
                  <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mb-1">
                    Direct Line
                  </span>
                  <Link to={`tel:${phone.replace(/[^\d+]/g, "")}`} className="text-foreground hover:text-primary transition-colors text-base font-medium font-default">
                    {phone}
                  </Link>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <HexIcon>
                  <MapPin className="size-4" />
                </HexIcon>
                <div className="flex flex-col">
                  <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mb-1">
                    Gauteng HQ
                  </span>
                  <span className="text-foreground text-base font-medium max-w-xs font-default">{address}</span>
                </div>
              </div>
            </div>

            <div className="flex flex-col gap-4 pt-8 border-t border-border">
              <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                Digital Network
              </span>
              <div className="flex flex-wrap gap-x-6 gap-y-3">
                {socialLinks.map((social, i) => {
                const Icon = social.icon;
                return <Link key={social.label} to={social.href} data-index={i} className="flex items-center gap-2 group">
                      <Icon className="size-4 text-foreground group-hover:text-primary transition-colors" />
                      <span className="font-mono text-[11px] uppercase tracking-widest text-foreground group-hover:text-primary transition-colors">
                        {social.label}
                      </span>
                    </Link>;
              })}
              </div>
            </div>
          </div>

          <div ref={rightRef} className={`lg:col-span-7 transition-all duration-700 ease-out delay-100 ${rightVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
            <FormProvider formKey="service_inquiry_form_field_services" formId="service_inquiry_form_field_services" sectionName="service-inquiry" defaultValues={{
            name: "",
            email: "",
            phone: "",
            inquiry_type: "",
            message: ""
          }} validationRules={{
            name: {
              required: true,
              minLength: 2
            },
            email: {
              required: true,
              pattern: "email"
            },
            phone: {
              pattern: "phone"
            },
            message: {
              required: true,
              minLength: 10
            }
          }} submitText="CONTACT AN EXPERT" successMessage="Transmission successful. An IMCNC Designs engineer will contact you within 24 hours." errorMessage="System error. Please verify your connection or contact us via direct email." className="bg-white rounded-md shadow-md p-8 md:p-12 border border-border">
              <ContactForm />
            </FormProvider>
          </div>

          <div className={`lg:col-span-12 transition-all duration-700 ease-out delay-200 ${rightVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
            <div className="mt-12 space-y-10">
              <div className="border-t border-border pt-10">
                <p className="font-default text-lg text-foreground/70 leading-relaxed mb-8"><br /></p>

                <div className="space-y-4">
                  <div className="flex items-center gap-2 mb-4">
                    <div className="w-2 h-6 bg-primary rounded-sm" />
                    <h3 className="font-default font-bold text-xl text-foreground uppercase tracking-tight">
                      What Happens During An On-Site Field Service?
                    </h3>
                  </div>
                  <ul className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {serviceTypes.map((service, i) => <li key={i} data-index={i} className="flex items-start gap-3 group">
                        <div className="mt-1.5 size-1.5 rounded-lg bg-primary group-hover:scale-125 transition-transform" />
                        <span className="font-default text-foreground/80 text-base">{service}</span>
                      </li>)}
                  </ul>
                </div>
              </div>

              <div className="border-t border-border pt-10">
                <div className="flex items-center gap-2 mb-6">
                  <div className="w-2 h-6 bg-primary rounded-sm" />
                  <h3 className="font-default font-bold text-xl text-foreground uppercase tracking-tight">
                    Benefits of Choosing Our Field Services
                  </h3>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {benefits.map((benefit, i) => <div key={i} data-index={i} className="flex items-center gap-4 p-4 bg-accent/5 rounded-md border border-border/50">
                      <CheckCircle2 className="size-5 text-primary shrink-0" />
                      <span className="font-default text-foreground font-medium">{benefit}</span>
                    </div>)}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}